/**
 * Each stack's contract on the dashboard, and whether Arcanum can invoice it
 * for credits.
 *
 * The rules live in `@/config/contract`. This file only says where the
 * dashboard reads each part from:
 *   - contract and partner: the portal's live record (`numa-client-metadata`),
 *     else the snapshot's `client_config.contract` and `partner`, else the
 *     snapshot's billing (`client_config.contract_type`).
 *   - billing: the snapshot's `client_config.contract_type`.
 *   - Inactive: the live tag, never the snapshot.
 */
import {
  CONTRACT_DISPLAY,
  contractBadge,
  isBilling,
  isCollectible,
  isContract,
  normalisePartner,
  resolveContract,
} from '@/config/contract';
import type { Billing, Contract } from '@/config/contract';
import type { ClientMetadata } from '@/types';
import type { ClientSnapshot } from '@/types/fleetAnalytics';

export interface StackContract {
  /** Undefined when nobody has set one and the snapshot carries no billing. */
  contract?: Contract;
  /** How the client is billed: the snapshot's `client_config.contract_type`. */
  billing?: Billing;
  /** The partner a partner client came through. Only on a partner contract. */
  partner?: string;
  /** The client carries the Inactive tag. */
  inactive: boolean;
  /** Arcanum can invoice the client for credits used past its allocation. */
  collectible: boolean;
  /** The badge a list shows: "Credits", "Partner: Ohnyx", "Trial, expired". Null when no contract is set. */
  badge: { label: string; variant: string; hint?: string } | null;
}

/** The contract of a stack. Every part of the dashboard that needs one takes this function. */
export type ContractOf = (s: ClientSnapshot) => StackContract;

/**
 * One stack's contract. `row` is the client's live record in the portal, and
 * `inactive` its live Inactive tag. With neither, the contract comes from the
 * snapshot alone and the stack counts as active.
 */
export function stackContract(
  s: ClientSnapshot | undefined,
  row?: Pick<ClientMetadata, 'contract' | 'partner' | 'trialEndDate'>,
  inactive = false
): StackContract {
  const cfg = s?.client_config;
  // The live record wins as a whole: its partner goes with its contract, never the snapshot's.
  const meta = isContract(row?.contract)
    ? row
    : {
        contract: isContract(cfg?.contract) ? cfg.contract : undefined,
        partner: cfg?.partner ?? undefined,
        trialEndDate: row?.trialEndDate,
      };
  const billing = isBilling(cfg?.contract_type) ? cfg.contract_type : undefined;
  const config = { contractType: billing };
  const contract = resolveContract(meta, config);
  const partner = contract === 'partner' && meta?.partner ? normalisePartner(meta.partner) : undefined;
  const badge = contractBadge(meta, config);
  return {
    contract,
    billing,
    partner: partner || undefined,
    inactive,
    collectible: isCollectible(contract, billing, inactive),
    badge: badge ? { ...badge, hint: contract ? CONTRACT_DISPLAY[contract].hint : undefined } : null,
  };
}

/** The contract read from the snapshot alone. Used when no live record is passed in. */
export const snapshotContract: ContractOf = (s) => stackContract(s);

/** The contract in a few words, for a sentence or a column: "Legacy", "Partner: Ohnyx", "No contract". */
export function contractWords(c: StackContract): string {
  return c.badge?.label ?? 'No contract';
}

/**
 * Why a stack's use past its allocation is not billed, in one sentence. Empty
 * for a stack Arcanum can invoice.
 */
export function notBilledReason(c: StackContract): string {
  if (c.collectible) return '';
  if (c.inactive) return 'This client is tagged Inactive, so credits it uses past its allocation are not billed.';
  if (!c.contract) return 'This client has no contract set, so credits it uses past its allocation are not billed.';
  if (c.contract === 'partner') {
    return `This client is on ${contractWords(c)} and is not billed as Credits, so credits it uses past its allocation are not billed.`;
  }
  return `This client is on ${contractWords(c)}, so credits it uses past its allocation are not billed.`;
}
